import { getOrdinalSuffixedNumber } from '@fruitful/fruitils';
import React, { type ReactNode } from 'react';
import {
    ArrowStraight,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    LabelRegular,
    Notice,
    Plus,
    SquarePenIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { EditableRuleSet } from '../utils/editableRuleSetUtils';
import type { EditableRule } from '../utils/flatRuleRows';
import { MONTHLY_EXPENSES_SET_ID } from '../utils/ruleSetIds';

// Presentational pieces shared by the native and web editable rule lists. A
// goal set is drawn as one gray card made of three kinds of row (header,
// goals, footer) so that goals can be dragged between sets in a single flat
// list; these are the parts of those rows that don't depend on the platform's
// drag and drop.

export const getSetTotals = (ruleSet: EditableRuleSet) => {
    const totalPercentage = ruleSet.rules.reduce(
        (sum, rule) => sum + (rule.percentage ?? 0),
        0,
    );
    return {
        totalPercentage,
        percentageNot100:
            ruleSet.rules.length > 0 && Math.abs(totalPercentage - 1) > 0.001,
    };
};

export const hasSetNotices = (
    ruleSet: EditableRuleSet,
    showStopWarning: boolean,
) =>
    getSetTotals(ruleSet).percentageNot100 ||
    (ruleSet.id !== MONTHLY_EXPENSES_SET_ID && ruleSet.rules.length === 0) ||
    showStopWarning;

export const SetNotices = ({
    ruleSet,
    showStopWarning,
}: {
    ruleSet: EditableRuleSet;
    showStopWarning: boolean;
}) => {
    const { totalPercentage, percentageNot100 } = getSetTotals(ruleSet);
    const isMonthlyExpenses = ruleSet.id === MONTHLY_EXPENSES_SET_ID;

    return (
        <>
            {percentageNot100 && (
                <Notice
                    variant="warning"
                    title={`Rules total is ${totalPercentage > 1 ? 'above' : 'below'} 100%`}
                    description="Combined rule contributions within a rule set must total 100%."
                />
            )}
            {!isMonthlyExpenses && ruleSet.rules.length === 0 && (
                <Notice
                    variant="warning"
                    title="Empty goal set"
                    description="Make sure there's at least one goal added to this goal set."
                />
            )}
            {showStopWarning && (
                <Notice
                    variant="warning"
                    title="Income Split stops here"
                    description="This rule doesn't include a target or limit, so all remaining funds will stop here and won't contribute to subsequent rules."
                />
            )}
        </>
    );
};

/** The non-reorderable "Monthly expenses" card above the goal sets. */
export const MonthlyExpensesCard = ({
    ruleSet,
    showStopWarning,
    renderRule,
}: {
    ruleSet: EditableRuleSet;
    showStopWarning: boolean;
    renderRule: (rule: EditableRule, percentageWarning: boolean) => ReactNode;
}) => {
    const { percentageNot100 } = getSetTotals(ruleSet);

    return (
        <YStack
            backgroundColor="$gray200"
            borderRadius={24}
            padding={16}
            gap={13}
        >
            <BodyMediumRegular>{ruleSet.name}</BodyMediumRegular>
            <YStack gap={8}>
                {ruleSet.rules.map((rule) => (
                    <React.Fragment key={rule.ruleId}>
                        {renderRule(rule, percentageNot100)}
                    </React.Fragment>
                ))}
            </YStack>
            <SetNotices ruleSet={ruleSet} showStopWarning={showStopWarning} />
        </YStack>
    );
};

/** What goes inside a set header's row: ordinal, name, edit button and `handle`. */
export const SetHeaderContent = ({
    ruleSet,
    ordinal,
    onEdit,
    handle,
}: {
    ruleSet: EditableRuleSet;
    ordinal: number;
    onEdit: () => void;
    handle?: ReactNode;
}) => (
    <>
        <View
            width={32}
            height={32}
            borderRadius={100}
            backgroundColor="$petalDarkest"
            alignItems="center"
            justifyContent="center"
        >
            <LabelRegular color="$petalLight">
                {getOrdinalSuffixedNumber(ordinal)}
            </LabelRegular>
        </View>
        <BodyMediumRegular flex={1}>{ruleSet.name}</BodyMediumRegular>
        <XStack alignItems="center">
            <View
                alignItems="center"
                justifyContent="center"
                minHeight={32}
                minWidth={32}
                cursor="pointer"
                onPress={onEdit}
            >
                <Icon icon={SquarePenIcon} size={16} color="$gray1200" />
            </View>
            {handle}
        </XStack>
    </>
);

/** The bottom of a set's card (notices, add goal) plus the arrow to the next set. */
export const SetFooterContent = ({
    ruleSet,
    isLast,
    showStopWarning,
    canAddGoals,
    onAddGoal,
}: {
    ruleSet: EditableRuleSet;
    isLast: boolean;
    showStopWarning: boolean;
    canAddGoals: boolean;
    onAddGoal: () => void;
}) => {
    const hasContent = canAddGoals || hasSetNotices(ruleSet, showStopWarning);

    return (
        <YStack>
            <YStack
                backgroundColor="$gray200"
                paddingHorizontal={16}
                // Rows above end with 8 of spacing; the card's own gap is 13.
                paddingTop={hasContent ? 5 : 0}
                paddingBottom={16}
                borderBottomLeftRadius={24}
                borderBottomRightRadius={24}
                gap={13}
            >
                <SetNotices
                    ruleSet={ruleSet}
                    showStopWarning={showStopWarning}
                />
                {canAddGoals && (
                    <Button
                        text="Add new goal"
                        variant={ButtonVariant.SECONDARY_ALT}
                        tone="neutral"
                        icon={Plus}
                        iconPosition="left"
                        size={ButtonSize.SMALL}
                        onPress={onAddGoal}
                    />
                )}
            </YStack>
            {!isLast && (
                <YStack alignSelf="center" padding={8}>
                    <ArrowStraight />
                </YStack>
            )}
        </YStack>
    );
};

// ---- While a goal is being dragged between sets -------------------------------
//
// Every set condenses: its goals become slim placeholders, and its notices,
// add button and the arrow to the next set are hidden, so that all the sets fit
// on screen and a goal can be dropped into any of them.

/** A slim stand-in for a goal card. */
export const GoalPlaceholder = () => (
    <View
        height={40}
        borderRadius={20}
        backgroundColor="$white"
        opacity={0.8}
    />
);

/** One placeholder's worth of a condensed set's card. */
export const GoalPlaceholderRow = () => (
    <YStack
        backgroundColor="$gray200"
        paddingHorizontal={16}
        paddingBottom={12}
    >
        <GoalPlaceholder />
    </YStack>
);

/**
 * The bottom of a condensed set's card, and the space before the next set.
 * A set with no goals (or only the one being dragged) shows a placeholder, so
 * it still reads as somewhere a goal can be dropped.
 */
export const CondensedSetFooter = ({ isEmpty }: { isEmpty: boolean }) => (
    <YStack>
        <YStack
            backgroundColor="$gray200"
            paddingHorizontal={16}
            paddingBottom={isEmpty ? 16 : 4}
            borderBottomLeftRadius={24}
            borderBottomRightRadius={24}
        >
            {isEmpty && <GoalPlaceholder />}
        </YStack>
        <View height={12} />
    </YStack>
);

/**
 * Where the goal being dragged would land.
 *
 * Drawn by a list that sizes it itself (native), it fills the space it is
 * given: a darker slot, the size of the goal card, within the set's card.
 *
 * Given `children` (web), it is instead the size of a placeholder, a darker one,
 * so the landing spot is the same size wherever it opens and the sets stay
 * condensed. `children` (the goal card) stays mounted but invisible and out of
 * the layout, so it keeps the drag handle the drag started from.
 */
export const GoalDropSlot = ({ children }: { children?: ReactNode }) =>
    children != null ? (
        <YStack
            backgroundColor="$gray200"
            paddingHorizontal={16}
            paddingBottom={12}
        >
            <View height={40} borderRadius={20} backgroundColor="$gray300" />
            <View
                position="absolute"
                top={0}
                left={0}
                right={0}
                opacity={0}
                pointerEvents="none"
            >
                {children}
            </View>
        </YStack>
    ) : (
        <YStack
            flex={1}
            backgroundColor="$gray200"
            paddingHorizontal={16}
            paddingBottom={8}
        >
            <View flex={1} backgroundColor="$gray300" borderRadius={16} />
        </YStack>
    );
